import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Activity, Siren, AlertOctagon, LogOut } from "lucide-react";
import { toast } from "sonner";

export default function ReceptionMobile() {
  const { user, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [hits, setHits] = useState([]);

  const refresh = async () => {
    try {
      const [v, e, h] = await Promise.all([
        api.get("/live-dashboard/visitors-on-site?limit=100"),
        api.get("/evacuation/active"),
        api.get("/watchlist-hits?status=pending"),
      ]);
      setItems(v.data?.items || []);
      setActive(e.data?.active ? e.data.event : null);
      setHits(h.data || []);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen bg-surface pb-6" data-testid="reception-mobile">
      <header className="sticky top-0 z-10 bg-navy-900 text-white px-5 py-4 flex items-center justify-between">
        <div>
          <div className="text-xs text-navy-200">Reception · {user?.name?.split(" ")[0]}</div>
          <div className="text-lg font-bold">Live front desk</div>
        </div>
        <button onClick={logout} className="p-2 rounded-full hover:bg-navy-700"><LogOut className="w-5 h-5" /></button>
      </header>

      {active && (
        <div className={`m-3 rounded-xl border-2 p-4 text-white animate-pulse ${active.is_drill ? "bg-warning-600 border-warning-700" : "bg-danger-600 border-danger-700"}`} data-testid="reception-evac-banner">
          <div className="flex items-center gap-2 font-bold uppercase tracking-wider text-sm">
            <Siren className="w-5 h-5" />
            {active.is_drill ? "Evacuation drill in progress" : "EVACUATION IN PROGRESS"}
          </div>
          <Link to="/evacuation/active" className="block mt-2 text-xs underline">Open evacuation board →</Link>
        </div>
      )}

      {hits.length > 0 && (
        <div className="m-3 rounded-xl bg-danger-50 border border-danger-200 p-4" data-testid="reception-hits-card">
          <div className="flex items-center gap-2 font-semibold text-danger-700">
            <AlertOctagon className="w-5 h-5" /> {hits.length} watchlist hit{hits.length === 1 ? "" : "s"} pending
          </div>
          <Link to="/admin/watchlist-hits" className="block mt-1 text-xs text-danger-700 underline">Review →</Link>
        </div>
      )}

      <main className="px-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-navy-700">
          <Activity className="w-4 h-4 text-accent" /> Visitors on site
          <span className="ml-auto text-xs text-navy-500">{items.length}</span>
        </div>
        {items.length === 0 ? (
          <div className="rounded-xl bg-white border border-navy-100 p-6 text-center text-sm text-navy-500">No active visitors.</div>
        ) : (
          <ul className="rounded-xl bg-white border border-navy-100 divide-y divide-navy-100">
            {items.map((v) => (
              <li key={v.id} className="px-4 py-3 flex items-center gap-3" data-testid={`recep-visitor-${v.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-navy-900 truncate">{v.visitor_name}</div>
                  <div className="text-xs text-navy-500 truncate">
                    {v.visitor_company || "—"} · host {v.host_name || "—"}
                  </div>
                </div>
                <span className="text-[10px] uppercase font-bold text-navy-600 bg-navy-50 px-2 py-0.5 rounded-full">
                  {v.visitor_type}
                </span>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
